import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import imgDemoBadge from "../../../assets/new_ui_img/demo.svg";
import GeoLogo from "./GeoLogo.jsx";

const envname = process.env.REACT_APP_ENV_TXT || null;
const isDemo = envname === "DEMO" || envname === "Engineering";

function useBasePath() {
  const location = useLocation();
  if (location.pathname.startsWith("/newDasboard")) return "/newDasboard";
  if (location.pathname.startsWith("/newDashboard")) return "/newDashboard";
  if (location.pathname.startsWith("/new")) return "/new";
  return "/newDashboard";
}

// ─── Header ───────────────────────────────────────────────────────────────────
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const basePath = useBasePath();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setCompanyOpen(false); }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCompanyOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const simpleLinks = [
    { label: "Home",       to: `${basePath}/home` },
    { label: "Solutions",  to: `${basePath}/solutions` },
    { label: "Technology", to: `${basePath}/technology` },
  ];

  const companyLinks = [
    { label: "Contact Us",          to: `${basePath}/contact` },
    { label: "Investor Relations",  to: `${basePath}/company` },
  ];

  const isActive = (to) => {
    if (to.endsWith("/home")) {
      return (
        location.pathname === basePath ||
        location.pathname === `${basePath}/` ||
        location.pathname.toLowerCase() === `${basePath}/home`.toLowerCase()
      );
    }
    return location.pathname.startsWith(to);
  };

  const isCompanyActive = companyLinks.some(({ to }) => location.pathname.startsWith(to));

  const navLinkClass = (active) =>
    `font-google-sans text-[16px] leading-tight transition-colors ${active ? "text-[#0a66c2] font-semibold" : "text-[#3f3f3f] hover:text-[#0a66c2]"}`;

  return (
    <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-sm shadow-[0_4px_10px_rgba(0,0,0,0.08)]" : "bg-white shadow-[0_4px_10px_rgba(0,0,0,0.08)]"}`}>
      <div className="max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between">
        <NavLink to={`${basePath}/home`} className="flex-shrink-0 flex items-center gap-2.5">
          <GeoLogo />
          {isDemo && (
            <img src={imgDemoBadge} alt="DEMO" className="h-[22px] w-auto inline-block select-none" />
          )}
        </NavLink>

        {/* Desktop right side: Nav + Login */}
        <div className="hidden md:flex items-center gap-10 lg:gap-[60px]">
          <nav className="flex items-center gap-6 lg:gap-8">
          {simpleLinks.map(({ label, to }) => (
            <NavLink
              key={label}
              to={to}
              className={navLinkClass(isActive(to))}
              style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}
            >
              {label}
            </NavLink>
          ))}

          {/* Company dropdown */}
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setCompanyOpen((o) => !o)}
              className={`flex items-center gap-[4px] font-google-sans text-[16px] leading-tight transition-colors ${isCompanyActive ? "text-[#0a66c2] font-semibold" : "text-[#3f3f3f] hover:text-[#0a66c2]"}`}
              style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}
              aria-haspopup="true"
              aria-expanded={companyOpen}
            >
              Company
              <svg className={`w-4 h-4 transition-transform duration-200 ${companyOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 16 16">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Dropdown panel */}
            <div className={`absolute top-[calc(100%+12px)] right-0 bg-white rounded-[8px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-gray-100 min-w-[180px] overflow-hidden transition-all duration-200 ${companyOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none"}`}>
              {companyLinks.map(({ label, to }) => (
                <NavLink
                  key={label}
                  to={to}
                  className={`block px-[18px] py-[12px] font-google-sans text-[15px] transition-colors whitespace-nowrap ${isActive(to) ? "text-[#0a66c2] bg-[#f0f7ff] font-semibold" : "text-[#3f3f3f] hover:bg-[#f5f5f5] hover:text-[#0a66c2]"}`}
                  style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}
                >
                  {label}
                </NavLink>
              ))}
            </div>
          </div>

          </nav>

          <NavLink to="/login" className="bg-[#0a66c2] text-white font-lato text-[16px] px-[28px] py-[8px] rounded-[5px] hover:bg-[#0856a8] transition-colors">
            Login
          </NavLink>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-0.5 bg-[#3f3f3f] transition-transform duration-300 ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
          <span className={`block w-6 h-0.5 bg-[#3f3f3f] transition-opacity duration-300 ${menuOpen ? "opacity-0" : ""}`} />
          <span className={`block w-6 h-0.5 bg-[#3f3f3f] transition-transform duration-300 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
        </button>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden bg-white border-t border-gray-100 overflow-hidden transition-all duration-300 ${menuOpen ? "max-h-[400px]" : "max-h-0"}`}>
        <nav className="px-6 py-4 flex flex-col gap-4">
          {simpleLinks.map(({ label, to }) => (
            <NavLink
              key={label}
              to={to}
              className={`font-google-sans text-[16px] ${isActive(to) ? "text-[#0a66c2] font-semibold" : "text-[#3f3f3f]"}`}
            >
              {label}
            </NavLink>
          ))}

          {/* Mobile Company submenu */}
          <div>
            <button
              onClick={() => setMobileCompanyOpen((o) => !o)}
              className={`flex items-center gap-[4px] font-google-sans text-[16px] w-full text-left ${isCompanyActive ? "text-[#0a66c2] font-semibold" : "text-[#3f3f3f]"}`}
            >
              Company
              <svg className={`w-4 h-4 transition-transform duration-200 ${mobileCompanyOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 16 16">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className={`overflow-hidden transition-all duration-200 ${mobileCompanyOpen ? "max-h-24 mt-2" : "max-h-0"}`}>
              {companyLinks.map(({ label, to }) => (
                <NavLink
                  key={label}
                  to={to}
                  className={`block pl-4 py-2 font-google-sans text-[15px] ${isActive(to) ? "text-[#0a66c2] font-semibold" : "text-[#6b6b6b]"}`}
                >
                  {label}
                </NavLink>
              ))}
            </div>
          </div>

          <NavLink to="/login" className="bg-[#0a66c2] text-white font-lato text-[16px] px-5 py-2 rounded-[5px] w-fit">
            Login
          </NavLink>
        </nav>
      </div>
    </header>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
export function Footer() {
  const basePath = useBasePath();

  const socialLinks = [
    {
      label: "LinkedIn",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M19 3C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19ZM18.5 18.5V13.2C18.5 12.3354 18.1565 11.5062 17.5452 10.8948C16.9338 10.2835 16.1046 9.94 15.24 9.94C14.39 9.94 13.4 10.46 12.92 11.24V10.13H10.13V18.5H12.92V13.57C12.92 12.8 13.54 12.17 14.31 12.17C14.6813 12.17 15.0374 12.3175 15.2999 12.5801C15.5625 12.8426 15.71 13.1987 15.71 13.57V18.5H18.5ZM6.88 8.56C7.32556 8.56 7.75288 8.383 8.06794 8.06794C8.383 7.75288 8.56 7.32556 8.56 6.88C8.56 5.95 7.81 5.19 6.88 5.19C6.43178 5.19 6.00193 5.36805 5.68499 5.68499C5.36805 6.00193 5.19 6.43178 5.19 6.88C5.19 7.81 5.95 8.56 6.88 8.56ZM8.27 18.5V10.13H5.5V18.5H8.27Z" fill="white" />
        </svg>
      ),
    },
    {
      label: "Facebook",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M22 12C22 6.48 17.52 2 12 2C6.48 2 2 6.48 2 12C2 16.84 5.44 20.87 10 21.8V15H8V12H10V9.5C10 7.57 11.57 6 13.5 6H16V9H14C13.45 9 13 9.45 13 10V12H16V15H13V21.95C18.05 21.45 22 17.19 22 12Z" fill="white" />
        </svg>
      ),
    },
    {
      label: "Instagram",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M13.0276 2C14.1526 2.003 14.7236 2.009 15.2166 2.023L15.4106 2.03C15.6346 2.038 15.8556 2.048 16.1226 2.06C17.1866 2.11 17.9126 2.278 18.5496 2.525C19.2096 2.779 19.7656 3.123 20.3216 3.678C20.8303 4.17773 21.2238 4.78247 21.4746 5.45C21.7216 6.087 21.8896 6.813 21.9396 7.878C21.9516 8.144 21.9616 8.365 21.9696 8.59L21.9756 8.784C21.9906 9.276 21.9966 9.847 21.9986 10.972L21.9996 11.718V13.028C22.002 13.7574 21.9944 14.4868 21.9766 15.216L21.9706 15.41C21.9626 15.635 21.9526 15.856 21.9406 16.122C21.8906 17.187 21.7206 17.912 21.4746 18.55C21.2238 19.2175 20.8303 19.8223 20.3216 20.322C19.8219 20.8307 19.2171 21.2242 18.5496 21.475C17.9126 21.722 17.1866 21.89 16.1226 21.94L15.4106 21.97L15.2166 21.976C14.7236 21.99 14.1526 21.997 13.0276 21.999L12.2816 22H10.9726C10.2429 22.0026 9.51312 21.9949 8.78359 21.977L8.58959 21.971C8.3522 21.962 8.11487 21.9517 7.87759 21.94C6.81359 21.89 6.08759 21.722 5.44959 21.475C4.78242 21.2241 4.17803 20.8306 3.67859 20.322C3.16954 19.8224 2.7757 19.2176 2.52459 18.55C2.27759 17.913 2.10959 17.187 2.05959 16.122L2.02959 15.41L2.02459 15.216C2.00616 14.4868 1.99782 13.7574 1.99959 13.028V10.972C1.99682 10.2426 2.00416 9.5132 2.02159 8.784L2.02859 8.59C2.03659 8.365 2.04659 8.144 2.05859 7.878C2.10859 6.813 2.27659 6.088 2.52359 5.45C2.77529 4.7822 3.16982 4.17744 3.67959 3.678C4.17875 3.16955 4.78278 2.77607 5.44959 2.525C6.08759 2.278 6.81259 2.11 7.87759 2.06C8.14359 2.048 8.36559 2.038 8.58959 2.03L8.78359 2.024C9.51278 2.00623 10.2422 1.99857 10.9716 2.001L13.0276 2ZM11.9996 7C10.6735 7 9.40174 7.52678 8.46406 8.46447C7.52638 9.40215 6.99959 10.6739 6.99959 12C6.99959 13.3261 7.52638 14.5979 8.46406 15.5355C9.40174 16.4732 10.6735 17 11.9996 17C13.3257 17 14.5974 16.4732 15.5351 15.5355C16.4728 14.5979 16.9996 13.3261 16.9996 12C16.9996 10.6739 16.4728 9.40215 15.5351 8.46447C14.5974 7.52678 13.3257 7 11.9996 7ZM11.9996 9C12.3936 8.99993 12.7837 9.07747 13.1477 9.22817C13.5117 9.37887 13.8424 9.5998 14.1211 9.87833C14.3997 10.1569 14.6207 10.4875 14.7715 10.8515C14.9224 11.2154 15 11.6055 15.0001 11.9995C15.0002 12.3935 14.9226 12.7836 14.7719 13.1476C14.6212 13.5116 14.4003 13.8423 14.1218 14.121C13.8432 14.3996 13.5126 14.6206 13.1486 14.7714C12.7847 14.9223 12.3946 14.9999 12.0006 15C11.2049 15 10.4419 14.6839 9.87927 14.1213C9.31666 13.5587 9.00059 12.7956 9.00059 12C9.00059 11.2044 9.31666 10.4413 9.87927 9.87868C10.4419 9.31607 11.2049 9 12.0006 9M17.2506 5.5C16.9191 5.5 16.6011 5.6317 16.3667 5.86612C16.1323 6.10054 16.0006 6.41848 16.0006 6.75C16.0006 7.08152 16.1323 7.39946 16.3667 7.63388C16.6011 7.8683 16.9191 8 17.2506 8C17.5821 8 17.9001 7.8683 18.1345 7.63388C18.3689 7.39946 18.5006 7.08152 18.5006 6.75C18.5006 6.41848 18.3689 6.10054 18.1345 5.86612C17.9001 5.6317 17.5821 5.5 17.2506 5.5Z" fill="white" />
        </svg>
      ),
    },
    {
      label: "Twitter / X",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M18.9 0H22.5806L14.5406 9.21257L24 21.7509H16.5943L10.7897 14.148L4.15543 21.7509H0.471429L9.07029 11.8937L0 0.00171426H7.59429L12.8331 6.94971L18.9 0ZM17.6057 19.5429H19.6457L6.48 2.09314H4.29257L17.6057 19.5429Z" fill="white" />
        </svg>
      ),
    },
    {
      label: "YouTube",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M10 15L15.19 12L10 9V15ZM21.56 7.17C21.69 7.64 21.78 8.27 21.84 9.07C21.91 9.87 21.94 10.56 21.94 11.16L22 12C22 14.19 21.84 15.8 21.56 16.83C21.31 17.73 20.73 18.31 19.83 18.56C19.36 18.69 18.5 18.78 17.18 18.84C15.88 18.91 14.69 18.94 13.59 18.94L12 19C7.81 19 5.2 18.84 4.17 18.56C3.27 18.31 2.69 17.73 2.44 16.83C2.31 16.36 2.22 15.73 2.16 14.93C2.09 14.13 2.06 13.44 2.06 12.84L2 12C2 9.81 2.16 8.2 2.44 7.17C2.69 6.27 3.27 5.69 4.17 5.44C4.64 5.31 5.5 5.22 6.82 5.16C8.12 5.09 9.31 5.06 10.41 5.06L12 5C16.19 5 18.8 5.16 19.83 5.44C20.73 5.69 21.31 6.27 21.56 7.17Z" fill="white" />
        </svg>
      ),
    },
  ];

  return (
    <footer className="bg-[#0d1117] border-t-[3px] border-[#0a66c2] relative">
      <div className="max-w-[1400px] mx-auto px-6 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div className="lg:col-span-2 flex flex-col gap-8">
            <div><GeoLogo dark /></div>
            <div>
              <h4 className="font-montserrat font-bold text-white text-[2rem] leading-[1.3] mb-3">
                {"Powering the Future of "}
                <br />
                Autonomous Parking
              </h4>
              <p className="font-lato text-[#8399b9] text-[1.1rem] leading-[1.3]">
                GeoParking connects property owners with Robotaxi operators through an AI-powered platform that simplifies parking management, maximizes efficiency, and unlocks new revenue opportunities.
              </p>
            </div>
            <div className="flex items-center gap-4">
              {socialLinks.map((s) => (
                <a key={s.label} href={s.to || "/"} aria-label={s.label} className="opacity-80 hover:opacity-100 transition-opacity shrink-0 inline-flex items-center justify-center">
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h5 className="font-inter font-semibold text-white text-[1rem] mb-6">Explore</h5>
            <ul className="font-lato text-[#8399b9] text-[1.1rem] space-y-4">
              {[
                { label: "Home",                to: `${basePath}/home` },
                { label: "Solutions",           to: `${basePath}/solutions` },
                { label: "Technology",          to: `${basePath}/technology` },
                { label: "Investor Relations",  to: `${basePath}/company` },
              ].map(({ label, to }) => (
                <li key={label}>
                  <NavLink
                    to={to}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="hover:text-white transition-colors"
                  >
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="font-inter font-semibold text-white text-[1rem] mb-6">Resources</h5>
            <ul className="font-lato text-[#8399b9] text-[1.1rem] space-y-4">
              {[
                { label: "Privacy Policy",                              to: "/privacy-policy" },
                { label: "Website Terms of Use",                        to: "/terms-of-use" },
                { label: "Parking Lot Management Terms of Service",     to: "/terms-of-service" },
              ].map(({ label, to }) => (
                <li key={label}>
                  <NavLink
                    to={to}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="hover:text-white transition-colors"
                  >
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-[#2c3a4e] mt-10 pt-6 flex flex-col md:flex-row justify-between gap-2">
          <p className="font-lato text-[#8399b9] text-[1.1rem] leading-[1.3]">
            © 2026 GeoParking Technologies, Inc. All rights reserved.
          </p>
          <p className="font-lato text-[#8399b9] text-[1.1rem] leading-[1.3]">
            Driving smarter mobility — one parking space at a time.
          </p>
        </div>
      </div>
    </footer>
  );
}

// ─── Scroll to top ────────────────────────────────────────────────────────────
export function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll to top"
      className={`fixed bottom-8 right-8 z-50 size-14 rounded-full bg-[#0a66c2] shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-[#0856a8] ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 19V5M5 12l7-7 7 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}
